import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Check, Search, User, Briefcase, GitBranch } from 'lucide-react';
import api from '../../api/client';
import toast from 'react-hot-toast';

export default function ConvertToCaseModal({ intake, onClose, onConverted }) {
  const [step, setStep] = useState(1); // 1=case type, 2=contact, 3=confirm
  const [caseTypes, setCaseTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [contactAction, setContactAction] = useState('create_new');
  const [contactId, setContactId] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    api.get('/intake/case-types').then(r => {
      setCaseTypes(r.data || []);
    }).catch(() => {});
  }, []);

  const hasLinkedContact = intake?.linkedContact;

  const handleSelectType = (rt) => {
    setSelectedType(rt);
    setSelectedWorkflow(null);
    // Auto-select workflow if only one or has default
    if (!rt.requires_workflow_selection) {
      const defaultWf = rt.workflows.find(w => w.is_default) || rt.workflows[0];
      if (defaultWf) setSelectedWorkflow(defaultWf);
    }
  };

  const canProceedFromStep1 = selectedType && (!selectedType.requires_workflow_selection || selectedWorkflow);

  const searchContacts = async () => {
    if (!contactSearch.trim()) return;
    try {
      const res = await api.post('/intake/check-duplicates', { first_name: contactSearch });
      setContactResults((res.data.matches || []).filter(m => m.record.record_type_slug === 'contact'));
    } catch { /* ignore */ }
  };

  const handleConvert = async () => {
    if (!selectedType) return;
    setConverting(true);
    try {
      const res = await api.post('/intake/convert-to-case', {
        intake_id: intake.id,
        case_type_slug: selectedType.slug,
        workflow_id: selectedWorkflow?.id || undefined,
        contact_action: contactAction,
        contact_id: contactId,
      });
      toast.success(`Case created: ${res.data.case_record_type}`);
      onConverted(res.data.case_id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Conversion failed');
    }
    setConverting(false);
  };

  const intakeTitle = intake?.title || 'Unknown';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Convert to Case</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {/* Step 1: Select case type + workflow */}
          {step === 1 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">What type of case is this?</h3>
              <div className="space-y-2 mb-4">
                {caseTypes.map(rt => (
                  <div key={rt.id}>
                    <label
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedType?.id === rt.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                      <input type="radio" name="case_type" checked={selectedType?.id === rt.id}
                        onChange={() => handleSelectType(rt)} className="text-indigo-600 focus:ring-indigo-500" />
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">{rt.name}</span>
                        {rt.description && <p className="text-xs text-gray-500">{rt.description}</p>}
                        {rt.workflows.length > 1 && (
                          <p className="text-xs text-indigo-500 mt-0.5">{rt.workflows.length} workflows available</p>
                        )}
                      </div>
                    </label>

                    {/* Workflow sub-selector */}
                    {selectedType?.id === rt.id && rt.requires_workflow_selection && (
                      <div className="ml-8 mt-2 mb-1 space-y-1.5">
                        <p className="text-xs font-medium text-gray-500 mb-1">Select matter type:</p>
                        {rt.workflows.map(wf => (
                          <label key={wf.id}
                            className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                              selectedWorkflow?.id === wf.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-100 hover:bg-gray-50'
                            }`}>
                            <input type="radio" name="workflow" checked={selectedWorkflow?.id === wf.id}
                              onChange={() => setSelectedWorkflow(wf)} className="text-indigo-600 focus:ring-indigo-500" />
                            <GitBranch className="w-3.5 h-3.5 text-gray-400" />
                            <div>
                              <span className="text-sm font-medium text-gray-800">{wf.name}</span>
                              {wf.description && <p className="text-xs text-gray-400">{wf.description}</p>}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <button onClick={() => setStep(2)} disabled={!canProceedFromStep1}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Contact handling */}
          {step === 2 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact record</h3>

              {hasLinkedContact ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700">Linked to: <strong>{intake.linkedContact.title}</strong></span>
                </div>
              ) : (
                <div className="space-y-2 mb-4">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${contactAction === 'create_new' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                    <input type="radio" name="contact" checked={contactAction === 'create_new'} onChange={() => setContactAction('create_new')} className="text-indigo-600" />
                    <span className="text-sm">Create new Contact for <strong>{intakeTitle}</strong></span>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${contactAction === 'link_existing' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                    <input type="radio" name="contact" checked={contactAction === 'link_existing'} onChange={() => setContactAction('link_existing')} className="text-indigo-600" />
                    <span className="text-sm">Search for existing Contact</span>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${contactAction === 'skip' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                    <input type="radio" name="contact" checked={contactAction === 'skip'} onChange={() => setContactAction('skip')} className="text-indigo-600" />
                    <span className="text-sm text-gray-500">Skip (no contact record)</span>
                  </label>
                </div>
              )}

              {contactAction === 'link_existing' && (
                <div className="mb-4">
                  <div className="flex gap-2 mb-2">
                    <input type="text" value={contactSearch} onChange={e => setContactSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchContacts()}
                      placeholder="Search by name..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <button onClick={searchContacts} className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200">
                      <Search className="w-4 h-4" />
                    </button>
                  </div>
                  {contactResults.map(m => (
                    <div key={m.record.id} onClick={() => setContactId(m.record.id)}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${contactId === m.record.id ? 'bg-indigo-50 border border-indigo-300' : 'hover:bg-gray-50 border border-gray-100'}`}>
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{m.record.title}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
                <button onClick={() => setStep(3)}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Confirm conversion</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Creating:</span>
                  <span className="font-medium">{selectedType?.name} for {intakeTitle}</span>
                </div>
                {selectedWorkflow && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Workflow:</span>
                    <span className="font-medium text-indigo-600">{selectedWorkflow.name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Contact:</span>
                  <span className="font-medium">
                    {hasLinkedContact ? intake.linkedContact.title :
                     contactAction === 'create_new' ? 'New contact will be created' :
                     contactAction === 'link_existing' && contactId ? 'Linked to existing' :
                     'No contact'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fields copied:</span>
                  <span className="text-gray-600">Date of Loss, Injury, Referral Source</span>
                </div>
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
                <button onClick={handleConvert} disabled={converting}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {converting ? 'Converting...' : 'Convert to Case'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
